// The sonar sweep's pass on WebGPU (card 59, R548, ui-map-interface.md
// §17.3.59).
//
// It is the twin of `gl/sweep-pass.ts` and it draws the same picture: one
// quad per running reveal, one program, no buffer of its own. A chart piece
// owns it, which is why it takes the WebGPU CONTEXT out of a `DrawCtx` and
// not an engine: a piece is a singleton in the registry and holds no engine.
//
// **It builds itself lazily and it heals itself.** The first `draw` makes the
// pipeline. A lost device destroys the shader module and every render
// pipeline built from it, and the context rebuilds both: the pipeline is a
// `GpuResource` the moment it is made, so the registry recreates it in
// registration order and no draw of this file asks whether the device is
// still the one it started on. A draw while the device is down is dropped,
// because `use()` answers false while the context is lost.
//
// The per-sweep loop needs no uniform buffer of its own either. The five
// shared uniforms are written once, each sweep writes its own four, and every
// `drawQuad` SNAPSHOTS the whole block into its own slice of the frame's
// uniform arena (the runtime design §2). So the loop reads the way the WebGL2
// one does, where a setter writes into the program object.
//
// `gfx/sweep.ts` is the dispatcher a piece talks to: it picks this class or
// the WebGL2 one from `gfx.backend`, and it owns the two shapes the caller
// fills in.

import { GpuPipeline } from './pipeline';
import { sweepProgram } from '../shaders/sweep';
import type { Gpu } from './context';
import type { Sweep, SweepStyle } from '../gfx/sweep';
import type { CameraUniforms } from '../layer';

export class GpuSweepPass {
  /** How many quads the last draw submitted. The test reads it. */
  draws = 0;

  private pipeline: GpuPipeline | null = null;
  private gpu: Gpu | null = null;

  /**
   * Draws every running sweep.
   *
   * `fade` is the chart's reading crossfade: the pass draws outside the
   * batches that carry it as a uniform, so it takes the same figure and
   * multiplies its own alpha by it.
   */
  draw(
    gpu: Gpu, cam: CameraUniforms,
    sweeps: readonly Sweep[], style: SweepStyle, fade: number,
  ): number {
    this.draws = 0;
    if (sweeps.length === 0) return 0;
    const p = this.ready(gpu);
    if (!p || !p.use()) return 0;
    // The five every sweep of this frame shares. They stay in the shadow, so
    // each draw below carries them into its own slice.
    p.uniformMatrix3fv('uViewProj', cam.viewProj);
    p.uniform4f('uStopAt', at(style.stopAt, 0), at(style.stopAt, 1),
      at(style.stopAt, 2), at(style.stopAt, 3));
    p.uniform4f('uStopAlpha', at(style.stopAlpha, 0), at(style.stopAlpha, 1),
      at(style.stopAlpha, 2), at(style.stopAlpha, 3));
    p.uniform2f('uLine', style.lineAlpha, style.lineWidth);
    p.uniform1f('uFade', fade);
    for (const s of sweeps) {
      if (s.r <= 0) continue;
      p.uniform2f('uCentre', s.x, s.y);
      p.uniform1f('uR', s.r);
      p.uniform1f('uAngle', s.angle);
      p.uniform3f('uRgb', s.rgb[0], s.rgb[1], s.rgb[2]);
      p.drawQuad();
      this.draws++;
    }
    return this.draws;
  }

  dispose(): void {
    this.pipeline?.dispose();
    this.pipeline = null;
    this.gpu = null;
    this.draws = 0;
  }

  // -- the private half ------------------------------------------------------

  /**
   * Builds the pipeline the first time, and again for another context.
   *
   * A device LOSS is no reason to build again: the pipeline registered with
   * the context, so the restore recreates it in place. A second context is,
   * because the four surfaces of the client each build an engine of their own
   * and a piece is one singleton for all of them.
   */
  private ready(gpu: Gpu): GpuPipeline | null {
    if (this.gpu === gpu && this.pipeline) return this.pipeline;
    this.pipeline?.dispose();
    this.gpu = gpu;
    this.pipeline = new GpuPipeline(gpu, sweepProgram());
    return this.pipeline;
  }
}

/** One stop of the ramp, or 0 where the style states fewer than four. */
function at(v: readonly number[], i: number): number {
  return v[i] ?? 0;
}
